import { logger } from '@automata/utils';
import { createSelector } from '@reduxjs/toolkit';

import type { LabwareByHandleIndexByTaskId } from './types';
import type { GraphEdges } from '../../types';
import { parseHandlePairId } from '../../utils/graph';
import { selectLabwareDependencyEdges } from '../graph/selectors';
import { selectLabwareIds, selectTaskIds } from '../workflow/selectors';

export function _nextTaskNodes(args: {
  nodeId: string /** can start from a labware node or task node */;
  handlePairId: string | null;
  labwareId: string;
  labwareDependencyEdges: GraphEdges;
}):
  | {
      handlesToLabwareId: Record<string, string>;
      taskId: string;
      handlePairId: string;
    }[]
  | null {
  const { nodeId, handlePairId, labwareId, labwareDependencyEdges } = args;

  const nextEdges = labwareDependencyEdges.filter((edge) => {
    if (edge.source !== nodeId) return false;
    const hasStringHandle = typeof edge.sourceHandle === 'string';
    const parsedSource = hasStringHandle
      ? parseHandlePairId(edge.sourceHandle as string)
      : null;

    if (hasStringHandle && parsedSource == null) {
      return false;
    }
    return hasStringHandle
      ? parsedSource === handlePairId
      : handlePairId === null;
  });

  if (nextEdges.length === 0) {
    return null;
  }

  const results = nextEdges.reduce<
    {
      handlesToLabwareId: Record<string, string>;
      taskId: string;
      handlePairId: string;
    }[]
  >((acc, edge) => {
    const parsed =
      edge.targetHandle != undefined
        ? parseHandlePairId(edge.targetHandle)
        : null;
    if (parsed == null) {
      return acc;
    }
    return [
      ...acc,
      {
        handlesToLabwareId: { [parsed]: labwareId },
        taskId: edge.target,
        handlePairId: parsed,
      },
    ];
  }, []);

  if (results.length === 0) {
    logger.error('bad handle id for edge', nextEdges);
    return null;
  }

  return results;
}

function traverseTaskNodesByLabwareHandle(args: {
  labwareId: string;
  initialNodeId: string;
  initialSourceHandle: string | null;
  maxDepth: number;
  labwareDependencyEdges: GraphEdges;
}): LabwareByHandleIndexByTaskId {
  const {
    initialNodeId,
    initialSourceHandle,
    maxDepth,
    labwareId,
    labwareDependencyEdges,
  } = args;
  // updates iterates a Record of task IDs to handle index to labware ID by traversing the graph starting from a given labware node and following edges up to a specified depth.
  const updates: LabwareByHandleIndexByTaskId = {};

  // queue is used to manage the traversal of the graph, starting from the initial node and handle, and exploring connected nodes up to the maxDepth.
  const queue: {
    nodeId: string;
    handlePairId: string | null;
    depth: number;
  }[] = [
    { nodeId: initialNodeId, handlePairId: initialSourceHandle, depth: 0 },
  ];

  while (queue.length > 0) {
    // Dequeue the next node to process, queue.shift()! asserts that the result is non-null.
    const { nodeId, handlePairId, depth } = queue.shift()!;

    if (depth >= maxDepth) {
      logger.error('Max labware node traversal depth exceeded');
      continue;
    }

    const nextNodes = _nextTaskNodes({
      nodeId,
      handlePairId,
      labwareId,
      labwareDependencyEdges,
    });

    if (!nextNodes?.length) continue;

    // For each next node, update the updates object with the labware ID and enqueue the next node for further traversal.
    for (const el of nextNodes) {
      updates[el.taskId] = {
        ...updates[el.taskId],
        ...el.handlesToLabwareId,
      };
      // Enqueue the next node for further traversal.
      queue.push({
        nodeId: el.taskId,
        handlePairId: el.handlePairId,
        depth: depth + 1,
      });
    }
  }
  return updates;
}

/** Task ID to source node index to labware ID. Eg: {"myTask": ["labwareA", null, "labwareB"]} */
export const selectLabwareIdsForTaskByTaskId = createSelector(
  selectLabwareIds,
  selectTaskIds,
  selectLabwareDependencyEdges,
  (
    labwareIds,
    taskIds,
    labwareDependencyEdges
  ): LabwareByHandleIndexByTaskId => {
    const perLabwareNode: Array<LabwareByHandleIndexByTaskId | null> =
      labwareIds.map((labwareId) => {
        // and if there is one edge, it targets a task.
        const labwareEdge = labwareDependencyEdges.find(
          (edge) => edge.source === labwareId
        );

        if (labwareEdge === undefined) {
          // this labware isn't connected to anything, ignore it
          return null;
        }

        return traverseTaskNodesByLabwareHandle({
          labwareId,
          initialNodeId: labwareId,
          initialSourceHandle: labwareEdge.sourceHandle
            ? parseHandlePairId(labwareEdge.sourceHandle)
            : null,
          maxDepth: 999,
          labwareDependencyEdges,
        });
      });

    const result: LabwareByHandleIndexByTaskId = {};

    // anneal the "fragments" per labware node into one object
    for (const taskId of taskIds) {
      for (const fragment of perLabwareNode) {
        if (fragment !== null) {
          result[taskId] = {
            ...result[taskId],
            ...fragment[taskId],
          };
        }
      }
    }

    return result;
  }
);
